import { Component, OnInit, ViewChild, ElementRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';

import { CustomIcon, IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ButtonMode, ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { IconPosition } from '@fcom/ui-components/components/icons/enums';
import { CmsContent, CMSelectionRules } from '@fcom/core-api';
import { ElementActions, ElementTypes, FragmentService, GaContext } from '@fcom/common';
import { LoginService } from '@fcom/common/login/services/login.service';
import { AppState, ConfigService } from '@fcom/core';
import { LoginStatus, LoginStep } from '@fcom/core-api/login/interfaces/login.interface';
import { loginStatus } from '@fcom/core/selectors/login.selector';
import { finShare } from '@fcom/rx';
import { stopPropagation } from '@fcom/core/utils/event';
import * as LoginActions from '@fcom/core/actions/login.actions';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';

import { HelpButtonStatus } from '../../interfaces';
import { AgentforceChatService } from '../../services/agentforce-chat.service';

@Component({
  selector: 'fin-pre-chat',
  templateUrl: 'pre-chat.component.html',
  styleUrls: ['pre-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PreChatComponent implements OnInit {
  private fragmentService = inject(FragmentService);
  private store$ = inject<Store<AppState>>(Store);
  private loginService = inject(LoginService);
  private configService = inject(ConfigService);
  private route = inject(Router);
  private gtmService = inject(GtmService);
  private afChatService = inject(AgentforceChatService);

  readonly ButtonMode = ButtonMode;
  readonly ButtonTheme = ButtonTheme;
  readonly chatExpandKey = 'chat-expand';
  readonly CustomIcon = CustomIcon;
  readonly ElementTypes = ElementTypes;
  readonly GaContext = GaContext;
  readonly HelpButtonStatus = HelpButtonStatus;
  readonly IconLibrary = IconLibrary;
  readonly IconPosition = IconPosition;
  readonly LoginStatus = LoginStatus;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @ViewChild('minimize', { static: false })
  minimizeButton: ElementRef;

  availableAccordions$: Observable<CmsContent[]>;
  loginStatus$: Observable<LoginStatus>;
  loginDialogIsEnabled = false;

  startUserForm = false;

  ngOnInit(): void {
    this.availableAccordions$ = this.fragmentService
      .getFragment<CMSelectionRules>('fragments.chatPreScreen.url')
      .pipe(map((collection): CmsContent[] => collection.items));
    setTimeout((): void => {
      this.minimizeButton.nativeElement.focus();
    }, 0);

    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());

    // check first if enableLoginDialog is true, then check if disabled on page
    if (this.configService.cfg.enableLoginDialog) {
      this.loginDialogIsEnabled = !this.checkIfLoginDialogDisabled(this.route.url);
    }
  }

  handleLogin = (e: MouseEvent): void => {
    stopPropagation(e);

    if (this.loginDialogIsEnabled) {
      this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
      this.trackElement('open-login-modal');
    } else {
      this.loginService.redirectToLogin();
      this.trackElement('redirect-to-login-link');
    }
  };

  // if url is included, loginDialog should be disabled
  private checkIfLoginDialogDisabled(pathUrl: string): boolean {
    return ['compensation-wizard'].some((page): boolean => pathUrl.includes(page));
  }

  openUserForm(): void {
    this.trackElement('chat-with-us-button');
    this.startUserForm = !this.startUserForm;

    // make sure the focus remains in the chat window
    Promise.resolve().then((): void => this.minimizeButton.nativeElement.focus());
  }

  setHelpButtonStatusVisible(): void {
    this.afChatService.chatButtonState$.next(HelpButtonStatus.VISIBLE);
  }

  public trackElement(label: string, type: ElementTypes = ElementTypes.BUTTON): void {
    this.gtmService.trackElement(
      `${label.replaceAll(' ', '-').toLocaleLowerCase()}`,
      GaContext.CHAT,
      type,
      undefined,
      ElementActions.CLICK
    );
  }
}
